import { useId } from 'react'
import { toggleClass } from '@/components/ui/toggle'
import { cn } from '@/lib/cn'
import { fmtInt } from '@/lib/format'

export interface SegmentOption<T extends string> {
  value: T
  label: string
  /** a count after the label (tabular figures) */
  count?: number
}

/**
 * One choice out of a few, always visible (design §6.12): a labelled `role="group"` of toggle
 * buttons with `aria-pressed`. The chosen one is the FilterChip formula with its ✓ — cyan tint,
 * cyan line, ink-1 600 (decision D1) — so the state reads without colour (rule 21). The label
 * stays visible above, like `Select`, so a filter row lines up (`hideLabel` keeps it for screen
 * readers only, e.g. in a toolbar). `md` (40 px, the default) matches `Input`/`Select`
 * `size="sm"` in a FilterBar. Wraps at 320 px.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  size = 'md',
  hideLabel,
  className,
}: {
  label: string
  value: T
  options: readonly SegmentOption<T>[]
  onChange: (value: T) => void
  disabled?: boolean
  size?: 'sm' | 'md'
  hideLabel?: boolean
  className?: string
}) {
  const id = useId()
  return (
    <div className={cn('min-w-0 print:hidden', className)}>
      <span id={id} className={hideLabel ? 'sr-only' : 'mb-1.5 block text-caption text-ink-2'}>
        {label}
      </span>
      <div role="group" aria-labelledby={id} className="flex flex-wrap gap-1">
        {options.map((o) => {
          const on = o.value === value
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              className={toggleClass(on, size)}
              onClick={() => onChange(o.value)}
            >
              {on && <span aria-hidden>✓</span>}
              {o.label}
              {o.count !== undefined && <span className="tnum font-normal">{fmtInt(o.count)}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
